import clsx from "clsx";
import { useMemo } from "react";

import { useProductContext } from "@/features/product/ProductProvider";
import { useTranslations } from "@/i18n/client";
import { Link } from "@/i18n/routing";
import type { ProductDetails } from "@/lib/centra/formatters";
import styles from "./index.module.css";

const VariantSelector = ({ product }: { product: ProductDetails }) => {
  const t = useTranslations();
  const { activeVariantName, setActiveVariant, activeVariant } =
    useProductContext();

  const sortedVariants = useMemo(() => {
    const sorted = !product?.variants
      ? undefined
      : product.variants
          .sort((a, b) => {
            const aSoldOut = a.soldOut ?? false;
            const bSoldOut = b.soldOut ?? false;

            if (aSoldOut === bSoldOut) return 0;
            return aSoldOut ? 1 : -1;
          })
          .sort((variantA, variantB) => {
            const displayItemIdAAsNumber =
              variantA?.displaySortOrder ||
              Number(variantA?.displayItemId || 0);

            const displayItemIdBAsNumber =
              variantB?.displaySortOrder ||
              Number(variantB?.displayItemId || 0);

            if (displayItemIdAAsNumber === displayItemIdBAsNumber) return 0;
            return displayItemIdAAsNumber < displayItemIdBAsNumber ? -1 : 1;
          })
          .filter(variant => product.gender === variant.gender)
          .filter(variant => product.isArchive === variant.isArchive)
          .filter(Boolean);

    if (product.gender) {
      return sorted?.filter(variant => variant.gender === product.gender);
    }

    return sorted;
  }, [product.gender, product.isArchive, product.variants]);

  if (!sortedVariants) return null;

  return (
    <div>
      <span className={styles.colorLabel}>
        {t("product.colour")}{" "}
        <span className={styles.colorName}>{activeVariant?.variantName}</span>
      </span>
      <div className={styles.productVariants}>
        {sortedVariants?.map((variant, i) => {
          if (variant.uri !== activeVariant?.uri)
            // If the variant is another display item, render as a link
            return (
              <Link href={variant.uri} key={i}>
                <button
                  title={variant?.name}
                  className={styles.button}
                  // onClick={() => {
                  //   setActiveVariant(variant.itemId);
                  // }}
                >
                  <div
                    className={clsx(
                      variant.variantName === activeVariantName &&
                        styles.selected,
                      styles.swatch,
                    )}
                  >
                    <div
                      style={{
                        backgroundColor: `${variant?.swatch?.hex}`,
                      }}
                    />
                  </div>
                </button>
              </Link>
            );

          return (
            <button
              key={i}
              title={variant?.name}
              className={styles.button}
              onClick={() => {
                if (activeVariant?.itemId === variant.itemId) return;
                setActiveVariant(variant.itemId);
              }}
            >
              <div
                className={clsx(
                  variant.variantName === activeVariantName && styles.selected,
                  styles.swatch,
                )}
              >
                <div
                  style={{
                    backgroundColor: `${variant?.swatch?.hex}`,
                  }}
                />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default VariantSelector;
